import React, {
  type CSSProperties,
  type ElementType,
  type FC,
  type ReactNode,
  type RefCallback,
  type RefObject,
  createElement,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useInView } from 'react-intersection-observer';
import {
  Ban,
  BookImage,
  File,
  FileArchive,
  FileAudio,
  FileChartLine,
  FileClock,
  FileCode,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileVideo,
  LucideFolder,
  type LucideIcon,
  OctagonAlert,
  Play,
  RotateCcwIcon,
  Trash2,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { PreviewUnavailable } from '@/components/preview-unavailable';
import { useAssetRating } from '@/hooks/data/assets/useAssetRating';
import { type Asset, ScanStatus, Variants } from '@/types/asset';
import { useCapabilities } from '@/hooks/useCapabilities';
import { useAsset } from '@/hooks/data/assets/useAsset';
import { Rating } from '@/components/rating';
import { FileType } from '@/types/fileType';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';
import NextImage from 'next/image';

function useExponentialPolling<T>({
  enabled,
  action,
  onSuccess,
  maxAttempts = 30,
  initialDelay = 1000,
  maxDelay = 5 * 60 * 1000, // Cap exponential backoff at 5 minutes
  resetKey,
  deps = [],
}: {
  enabled: boolean;
  action: () => Promise<T>;
  onSuccess: (data: T) => void;
  maxAttempts?: number;
  initialDelay?: number;
  maxDelay?: number;
  resetKey?: unknown;
  deps?: Array<unknown>;
}) {
  const [attempt, setAttempt] = useState(0);
  const [delay, setDelay] = useState(initialDelay);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setAttempt(0);
    setDelay(initialDelay);
  }, [initialDelay, maxAttempts, resetKey]);

  useEffect(() => {
    if (!enabled || attempt >= maxAttempts) {
      return;
    }
    timerRef.current = setTimeout(() => {
      void action().then((data) => {
        onSuccess(data);
        setAttempt((a) => a + 1);
        setDelay((d) => Math.min(d * 2, maxDelay));
      });
    }, delay);
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
    // `deps` is deliberately dynamic; the rule can't verify a spread.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, attempt, delay, action, maxAttempts, maxDelay, onSuccess, ...deps]);
}

export type AssetThumbnailProps = {
  id?: string;
  asset?: Asset;
  name?: string;
  type?: string | null;
  thumbnail?: string;
  variantKey?: string;
  draggable?: boolean;
  width?: number;
  height?: number;
  imageRef?: RefObject<HTMLImageElement> | RefCallback<HTMLImageElement>;
  imageContainerRef?: RefCallback<HTMLDivElement>;
  iconContainerRef?: RefObject<HTMLImageElement>;
  overlayIcon?: LucideIcon;
  iconStrokeWidth?: number;
  fill?: boolean;
  onLoad?: () => void;
  imageClassName?: string;
  iconClassName?: string;
  imageContainerStyle?: CSSProperties;
  imageContainerClassName?: string;
  iconContainerClassName?: string;
  playIconClassName?: string;
  overlayIconClassName?: string;
  onDoubleClick?: () => void;
  renderVideoPlayer?: boolean;
  hasDescription?: boolean;
  originalName?: string;
  previewUnavailable?: boolean;
  thumbnailPollAttempts?: number;
  scanPollAttempts?: number;
  disablePolling?: boolean;
  dataCy?: string;
  isListView?: boolean;
  htmlImage?: boolean;
  overlay?: ReactNode;
};

// `alt` is always supplied by the two call sites below; the rule cannot see through the spread.
// eslint-disable-next-line @next/next/no-img-element, jsx-a11y/alt-text
const Image = (props: any) => <img {...props} />;
export const AssetThumbnail: FC<AssetThumbnailProps> = memo(
  ({
    asset,
    id,
    thumbnail: initialThumbnail,
    name,
    type,
    draggable = false,
    width,
    height,
    imageRef,
    imageContainerRef,
    iconContainerRef,
    overlay,
    overlayIcon: Icon,
    iconStrokeWidth = 1,
    fill = false,
    imageClassName = '',
    iconClassName = '',
    imageContainerClassName = 'relative',
    imageContainerStyle = null,
    iconContainerClassName = '',
    playIconClassName = '',
    overlayIconClassName = '',
    onDoubleClick,
    onLoad,
    renderVideoPlayer = false,
    hasDescription = false,
    originalName,
    previewUnavailable = false,
    thumbnailPollAttempts = 30,
    scanPollAttempts = 30,
    variantKey,
    disablePolling = false,
    dataCy,
    isListView = false,
    htmlImage = false,
  }) => {
    const tLabels = useTranslations('labels');
    const tErrors = useTranslations('errors');
    const queryClient = useQueryClient();

    // Intersection Observer hook to only fetch when in view
    const { ref: inViewRef, inView } = useInView({
      threshold: 0.1, // Trigger when 10% of the component is visible
      triggerOnce: false, // Set to false so that every time the element enters view, inView becomes true
    });
    const { setRating, isPending: isRatingPending } = useAssetRating();
    const { canEditAsset } = useCapabilities();

    const [imageError, setImageError] = useState(false);
    const [shouldPoll, setShouldPoll] = useState(false);
    const [shouldScanPoll, setShouldScanPoll] = useState(false);
    const hasRetried = useRef(false);

    const sanitizedId = id === null || disablePolling ? undefined : id;
    const { asset: fetchedAsset, refetchAsset } = useAsset(sanitizedId, {
      enabled: false,
      refetchOnWindowFocus: false,
      suppressGlobalError: true,
    });

    const selectedKey = variantKey ?? Variants.ThumbnailInternal;

    // Folder lists sometimes give variants: [] – guard for that
    const readVariant = (a?: Asset) => {
      const v = a?.variants as any;
      if (!v || Array.isArray(v) || typeof v !== 'object') {
        return undefined;
      }
      return v[selectedKey];
    };

    const effectiveAsset = (fetchedAsset as Asset) ?? asset;
    const hasVersion = Boolean(effectiveAsset?.type);

    const seenVersionRef = useRef(false);
    if (hasVersion) {
      seenVersionRef.current = true;
    }

    const variantNow = readVariant(effectiveAsset);

    const urlNow = variantNow?.url as string | undefined;

    const [thumbnail, setThumbnail] = useState<
      { url: string | null; width?: number; height?: number } | null | undefined
    >(
      initialThumbnail
        ? { url: initialThumbnail }
        : // If the list gave an object with url, use it, otherwise leave null and let poll decide
          variantNow && 'url' in variantNow
          ? variantNow
          : null,
    );

    // This makes sure the thumbnail is up to date with the current asset.
    useEffect(() => {
      const nextThumbnail = initialThumbnail
        ? { url: initialThumbnail }
        : variantKey
          ? asset?.variants?.[variantKey]
          : asset?.variants?.[Variants.ThumbnailInternal];

      if (typeof nextThumbnail === 'undefined' && !asset && !initialThumbnail) {
        return;
      }

      setThumbnail(nextThumbnail);
    }, [asset, initialThumbnail, variantKey]);

    useEffect(() => {
      setImageError(false);
    }, [thumbnail?.url, id]);

    useEffect(() => {
      hasRetried.current = false;
    }, [id]);

    // Use the freshest URL no matter where it came from
    const finalThumbUrl = useMemo(() => urlNow ?? thumbnail?.url ?? undefined, [thumbnail?.url, urlNow]);
    const hasThumbnail = Boolean(finalThumbUrl);

    const scanStatus = effectiveAsset?.scanStatus as ScanStatus | undefined;

    const isNeverThumbnailType = effectiveAsset?.type === FileType.Other;

    useEffect(() => {
      if (hasThumbnail || disablePolling) {
        // we already have a thumbnail or we specifically disabled polling, stop all polling
        setShouldScanPoll(false);
        setShouldPoll(false);
      } else if (isNeverThumbnailType) {
        // FileType.Other never gets a thumbnail, regardless of what variants says
        setShouldScanPoll(false);
        setShouldPoll(false);
      } else {
        if (!hasVersion) {
          // We don't even have the type yet, scan polling on, thumbnail polling off
          setShouldScanPoll(true);
          setShouldPoll(false);
        } else {
          // We have the type, thumbnail decides the rest
          if (typeof thumbnail === 'undefined') {
            // Asset will never have a thumbnail, stop both
            setShouldScanPoll(false);
            setShouldPoll(false);
          } else if (thumbnail === null) {
            // Asset should have a thumbnail but it's not ready yet, thumbnail polling only
            setShouldScanPoll(false);
            setShouldPoll(true);
          }
        }
      }
    }, [hasThumbnail, thumbnail, hasVersion, isNeverThumbnailType, disablePolling]);

    // ScanStatus polling - disabled, relying on WebSocket
    useExponentialPolling({
      enabled: false, // (inView || Boolean(imageContainerRef)) && Boolean(id) && shouldScanPoll && !disablePolling,
      action: refetchAsset,
      onSuccess: (fresh) => {
        const freshAsset = fresh.data! as Asset;

        // If scan status advanced beyond Unscanned, useEffect will turn scan polling off
        // but update caches so parents can render richer data
        queryClient.setQueryData<Asset>(['asset', freshAsset?.id], (prev) =>
          prev ? { ...prev, ...freshAsset } : freshAsset,
        );
      },
      maxAttempts: scanPollAttempts,
      resetKey: id,
      deps: [inView, id, shouldScanPoll, refetchAsset],
    });

    // Thumbnail polling - disabled, relying on WebSocket
    useExponentialPolling({
      enabled: false, // (inView || Boolean(imageContainerRef)) && Boolean(id) && shouldPoll && !disablePolling,
      action: refetchAsset,
      onSuccess: (fresh) => {
        const freshAsset = fresh.data! as Asset;

        const variant = variantKey
          ? freshAsset.variants?.[variantKey]
          : freshAsset.variants?.[Variants.ThumbnailInternal];

        if (variant === null) {
          // Should exist but not ready, keep polling
          setThumbnail(null);
        } else if (variant?.url) {
          // Ready, stop polling useEffect will flip flags off
          setThumbnail(variant);
        } else {
          // undefined, thumbnail will never exist, stop polling
          setThumbnail(undefined);
        }

        // Overwrite the asset in the cache
        queryClient.setQueryData<Asset>(['asset', freshAsset?.id], (prev) =>
          prev ? { ...prev, ...freshAsset } : freshAsset,
        );

        // Overwrite the asset in the assetData cache
        queryClient.setQueriesData(
          {
            predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === 'assetData',
          },
          (root: any) => {
            if (!root) {
              return root;
            }

            // Folder/Album payload: { assets: { assets: Asset[], pagination } }
            if (root?.assets?.assets && Array.isArray(root.assets.assets)) {
              const arr: Array<Asset> = root.assets.assets;
              const idx = arr.findIndex((a) => a?.id === freshAsset.id);

              if (idx === -1) {
                return root;
              } // Asset is not on this page

              const next = arr.slice();
              next[idx] = { ...arr[idx], ...freshAsset };

              return { ...root, assets: { ...root.assets, assets: next } };
            }

            // Other shapes of assetData
            if (Array.isArray(root?.assets)) {
              const arr: Array<Asset> = root.assets;
              const idx = arr.findIndex((a) => a?.id === freshAsset.id);

              if (idx === -1) {
                return root;
              }

              const next = arr.slice();
              next[idx] = { ...arr[idx], ...freshAsset };

              return { ...root, assets: next };
            }

            if (Array.isArray(root?.data?.items)) {
              const arr: Array<Asset> = root.data.items;
              const idx = arr.findIndex((a) => a?.id === freshAsset.id);

              if (idx === -1) {
                return root;
              }

              const next = arr.slice();
              next[idx] = { ...arr[idx], ...freshAsset };

              return { ...root, data: { ...root.data, items: next } };
            }

            return root;
          },
        );
      },
      maxAttempts: thumbnailPollAttempts,
      resetKey: `${id ?? ''}:${variantKey ?? ''}`,
      deps: [inView, id, shouldPoll, refetchAsset],
    });

    const imageVariant = asset?.variants?.[Variants.ThumbnailInternal]?.url ?? thumbnail?.url;
    const videoVariant = asset?.variants?.[Variants.VideoPreviewInternal]?.url;

    const handleImageError = useCallback(async () => {
      if (finalThumbUrl && sanitizedId && !hasRetried.current) {
        try {
          const parsed = new URL(finalThumbUrl, window.location.origin);
          const expires = parsed.searchParams.get('expires');
          if (expires !== null && Number(expires) < Date.now() / 1000) {
            hasRetried.current = true;
            const result = await refetchAsset();
            const freshAsset = result?.data as Asset | undefined;
            const freshVariant = freshAsset
              ? variantKey
                ? freshAsset.variants?.[variantKey]
                : freshAsset.variants?.[Variants.ThumbnailInternal]
              : undefined;
            if (freshVariant?.url) {
              setThumbnail(freshVariant);
              return;
            }
          }
        } catch {
          // URL parsing failed, fall through to error state
        }
      }
      setImageError(true);
    }, [finalThumbUrl, sanitizedId, refetchAsset, variantKey]);

    // const prevInView = useRef(false);

    // useEffect(() => {
    //   // Scrolled into view again
    //   if (!prevInView.current && inView) {
    //     // Restart Scan polling if we still don't have a clean scan
    //     if (!hasVersion && scanStatus === ScanStatus.Unscanned && !shouldScanPoll) {
    //       setScanAttempt(1);
    //       setScanPollDelay(1000);
    //       setShouldScanPoll(true);
    //     }

    //     // Thumbnail poll restart if we still don't have a thumbnail
    //     const haveThumbNow =
    //       Boolean(variantKey ? asset?.variants?.[variantKey]?.url : asset?.variants?.[Variants.ThumbnailInternal]?.url) ||
    //       Boolean(initialThumbnail);
    //     if (((!hasVersion && scanStatus === ScanStatus.Clean) || (hasVersion && !haveThumbNow)) && !shouldPoll) {
    //       setThumbnailAttempt(1);
    //       setPollDelay(1000);
    //       setShouldPoll(true);
    //     }
    //   }

    //   prevInView.current = inView;
    // }, [inView, hasVersion, scanStatus, shouldScanPoll, shouldPoll, asset, initialThumbnail, variantKey]);

    // Same shape as QuarantineOverlay below, deliberately: a wash plus one white glyph is
    // already this product's language for "something is wrong with this asset", and the
    // thumbnail is the one place that reads identically in the grid and the list.
    //
    /**
     * Red bin = trashed (still restorable); heavier red wash + ban glyph = deleted for
     * good, past acting on. Green + RotateCcwIcon = restored - the same glyph the
     * selection bar, contextual menu and empty state use for Restore.
     */
    const AssetStateOverlay = ({ state }: { state: 'trashed' | 'deleted' | 'restored' }) => {
      const isRestored = state === 'restored';
      const isDeleted = state === 'deleted';
      const StateIcon = isRestored ? RotateCcwIcon : isDeleted ? Ban : Trash2;

      return (
        <div className="pointer-events-none absolute inset-0 z-20" data-cy={`asset-state-overlay-${state}`}>
          <div
            className={cn(
              'absolute inset-0',
              isRestored
                ? 'bg-status-success/25'
                : isDeleted
                  ? 'bg-status-failure-overlay-dark'
                  : 'bg-status-failure-overlay',
            )}
          />
          <StateIcon
            className={cn(
              'absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 transform stroke-white',
              overlayIconClassName,
            )}
            strokeWidth={1.5}
            aria-label={tLabels(state === 'deleted' ? 'deleted' : isRestored ? 'restored' : 'movedToTrash')}
          />
        </div>
      );
    };

    const QuarantineOverlay = () => (
      <div className="pointer-events-none absolute inset-0 z-20">
        <div className="absolute inset-0 bg-status-failure-overlay" />
        <OctagonAlert
          className={cn(
            'absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 transform stroke-white',
            overlayIconClassName,
          )}
          strokeWidth={1.5}
          aria-label={tLabels('quarantined')}
        />
      </div>
    );

    const renderImage = (src: string) => (
      <>
        {(type === FileType.Video || type === 'video/mp4') && (
          <Play
            className={cn(
              'absolute z-10 stroke-white drop-shadow-[1px_1px_3px_hsla(0,0%,0%,0.25)]',
              playIconClassName ? playIconClassName : 'bottom-4 left-3',
            )}
            strokeWidth={1.5}
          />
        )}
        {Icon && (
          <Icon
            className={cn(
              'absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 transform stroke-white',
              overlayIconClassName,
            )}
            strokeWidth={1.5}
          />
        )}
        {htmlImage ? (
          <Image
            width={!fill ? (width ?? (variantKey ? (asset?.variants?.[variantKey]?.width ?? 200) : 200)) : undefined}
            height={!fill ? (height ?? (variantKey ? (asset?.variants?.[variantKey]?.height ?? 200) : 200)) : undefined}
            ref={imageRef}
            alt={name ?? asset?.name ?? ''}
            onError={handleImageError}
            onLoad={onLoad}
            src={src}
            draggable={draggable}
            fill={fill ? 'true' : undefined}
            className={imageClassName}
            onDoubleClick={onDoubleClick}
          />
        ) : (
          <NextImage
            width={!fill ? (width ?? (variantKey ? (asset?.variants?.[variantKey]?.width ?? 200) : 200)) : undefined}
            height={!fill ? (height ?? (variantKey ? (asset?.variants?.[variantKey]?.height ?? 200) : 200)) : undefined}
            ref={imageRef}
            alt={name ?? asset?.name ?? ''}
            onError={handleImageError}
            onLoad={onLoad}
            src={src}
            draggable={draggable}
            fill={fill}
            className={imageClassName}
            onDoubleClick={onDoubleClick}
          />
        )}
      </>
    );

    const renderVideo = () => (
      <video
        preload="none"
        width={width}
        height={height}
        poster={asset?.variants?.[Variants.PreviewInternal]?.url ?? imageVariant ?? undefined}
        controls
        className={imageClassName}
      >
        <source src={videoVariant ?? undefined} type="video/mp4" />
        {tErrors('videoTagUnsupported')}
      </video>
    );

    // Memoize icon selection
    const IconComponent: ElementType = useMemo(() => {
      switch (type) {
        case FileType.Image:
        case 'image/jpeg':
        case 'image/jpg':
        case 'image/png':
          return FileImage;
        case FileType.Video:
          return FileVideo;
        case FileType.Audio:
          return FileAudio;
        case FileType.Document:
          return FileText;
        case FileType.Folder:
          return LucideFolder;
        case FileType.Album:
          return BookImage;
        case 'video/mp4':
          return FileVideo;
        case 'text/plain':
        case 'application/rtf':
        case 'application/pdf':
        case 'application/msword':
        case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
          return FileText;
        case 'application/vnd.ms-powerpoint':
        case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
          return FileChartLine;
        case 'text/csv':
        case 'application/vnd.ms-excel':
        case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
          return FileSpreadsheet;
        case 'application/zip':
        case 'application/gzip':
        case 'application/x-rar-compressed':
          return FileArchive;
        case 'text/xml':
        case 'application/xml':
          return FileCode;
        case FileType.Other:
        default:
          return File;
      }
    }, [type]);

    const renderIcon = useCallback(
      (IconOverride?: ElementType, classNameOverride?: string) => (
        <>
          {Icon && (
            <Icon
              className={cn(
                'absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 transform stroke-white',
                overlayIconClassName,
              )}
              strokeWidth={1.5}
            />
          )}
          <div ref={iconContainerRef} className={iconContainerClassName}>
            {createElement(IconOverride ?? IconComponent, {
              className: cn(iconClassName, classNameOverride),
              strokeWidth: iconStrokeWidth,
            })}
          </div>
        </>
      ),
      [
        Icon,
        overlayIconClassName,
        iconContainerRef,
        iconContainerClassName,
        IconComponent,
        iconClassName,
        iconStrokeWidth,
      ],
    );

    const showLoadingIcon = shouldPoll || shouldScanPoll;
    const showQuarantineOverlay = scanStatus && scanStatus === ScanStatus.Quarantined;

    // Set by a live broadcast for a row still on screen, and cleared by the refresh that
    // removes it. The flag carries its own reason, so this renders once per cell without
    // reaching for the router - AssetThumbnail is memoised and there is one per grid cell,
    // so a hook here would cost a subscription and a route parse on every one.
    const assetState = asset?.deleted ? 'deleted' : asset?.departed;

    const url = finalThumbUrl ?? thumbnail?.url;

    const baseContent = showLoadingIcon
      ? renderIcon(FileClock)
      : renderVideoPlayer && videoVariant
        ? renderVideo()
        : url && !imageError
          ? renderImage(url)
          : renderIcon();

    return (
      <div
        ref={imageContainerRef}
        className={cn('relative h-full', imageContainerClassName, imageError && 'w-full')}
        style={imageContainerStyle ?? {}}
        data-cy={dataCy}
      >
        <div ref={inViewRef} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} />
        {/* Candystripe progress bar */}
        {showLoadingIcon && (
          <div
            className={cn(
              'animate-candystripe-move pointer-events-none absolute inset-x-0 bottom-0 z-10 bg-[linear-gradient(-45deg,hsl(var(--ui-separator))_25%,hsl(var(--ui-void-gap))_25%,hsl(var(--ui-void-gap))_50%,hsl(var(--ui-separator))_50%,hsl(var(--ui-separator))_75%,hsl(var(--ui-void-gap))_75%,hsl(var(--ui-void-gap)))]',
              isListView
                ? 'h-1 animate-[candystripe-move_4s_linear_infinite] bg-size-[12.5px_12.5px]'
                : 'h-2 animate-[candystripe-move_2s_linear_infinite] bg-size-[25px_25px]',
            )}
          />
        )}

        {previewUnavailable && asset?.variants?.[Variants.ThumbnailInternalMedium]?.url ? (
          <PreviewUnavailable
            thumbnailUrl={asset.variants[Variants.ThumbnailInternalMedium].url}
            width={asset.variants[Variants.ThumbnailInternalMedium].width ?? 200}
            height={asset.variants[Variants.ThumbnailInternalMedium].height ?? 200}
          />
        ) : (
          baseContent
        )}

        {overlay}

        {showQuarantineOverlay && <QuarantineOverlay />}

        {assetState && <AssetStateOverlay state={assetState} />}

        {hasDescription && (
          <div className="mx-auto mt-2 flex w-0 min-w-full items-center gap-x-5">
            <div className="z-2 flex min-w-0 flex-1 flex-col text-left">
              {name && (
                <div
                  className="line-clamp-1 text-sm font-bold wrap-anywhere text-text-base"
                  title={name}
                  data-cy="asset-name"
                >
                  {name}
                </div>
              )}
              {originalName && (
                <div
                  className="z-2 line-clamp-1 text-xs wrap-anywhere text-text-gray-alternate"
                  title={originalName}
                  data-cy="asset-original-name"
                >
                  {originalName}
                </div>
              )}
            </div>
            {asset && (
              <Rating
                readOnly={!canEditAsset}
                rating={asset.rating}
                isLoading={isRatingPending}
                onChange={(rating) => setRating(asset.id, asset.rating ?? 0, rating)}
              />
            )}
          </div>
        )}
      </div>
    );
  },
);

AssetThumbnail.displayName = 'AssetThumbnail';
